import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import axiosClient from '../clients/axiosClient';
import { msUntilRefresh, refreshAuthTokens } from '@shared/auth/tokenRefresh';
import { useNavigate } from 'react-router-dom';
import { setReporterUser } from '../reporting/reporter';
import { clearFaroUserContext, setFaroUserContext } from '../reporting/faro';

interface User {
  id: string;
  username: string;
  email: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

interface AuthSession {
  user: User;
  token: string;
  refreshToken: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  /** Identifier is sent as `username` in the API body (ledger accepts email or username). */
  login: (identifier: string, password: string) => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string
  ) => Promise<void>;
  setSession: (session: AuthSession) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Configure axios defaults
  // Use relative URLs - Vite proxy handles /api in dev, same-origin works in production
  axiosClient.defaults.baseURL = '';
  axiosClient.defaults.withCredentials = true; // Required for CORS with credentials

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      let parsed: User;
      try {
        parsed = JSON.parse(storedUser) as User;
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        setLoading(false);
        return;
      }
      setToken(storedToken);
      setUser(parsed);
      setReporterUser(parsed?.id);
      if (parsed?.id) {
        setFaroUserContext({
          id: parsed.id,
          username: parsed.username,
          email: parsed.email,
          role: parsed.role,
        });
      }
      axiosClient.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${storedToken}`;

      // Verify token is still valid
      axiosClient
        .get('/api/auth/me')
        .then((response) => {
          setUser(response.data.user);
          setReporterUser(response.data.user?.id);
          if (response.data.user?.id) {
            setFaroUserContext({
              id: response.data.user.id,
              username: response.data.user.username,
              email: response.data.user.email,
              role: response.data.user.role,
            });
          }
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setUser(null);
          setReporterUser(undefined);
          clearFaroUserContext();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const setSession = ({ user: nextUser, token: nextToken, refreshToken }: AuthSession) => {
    setUser(nextUser);
    setToken(nextToken);
    setReporterUser(nextUser?.id);
    if (nextUser?.id) {
      setFaroUserContext({
        id: nextUser.id,
        username: nextUser.username,
        email: nextUser.email,
        role: nextUser.role,
      });
    }
    localStorage.setItem('token', nextToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    localStorage.setItem('refreshToken', refreshToken);
    axiosClient.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
  };

  const login = async (identifier: string, password: string) => {
    try {
      const response = await axiosClient.post('/api/auth/login', {
        username: identifier.trim(),
        password,
      });
      const { user, token, refreshToken } = response.data;
      setSession({ user, token, refreshToken });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (
    username: string,
    email: string,
    password: string
  ) => {
    try {
      const response = await axiosClient.post('/api/auth/register', {
        username,
        email,
        password,
      });
      const { user, token, refreshToken } = response.data;
      setSession({ user, token, refreshToken });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      axiosClient.post('/api/auth/logout', { refreshToken }).catch(() => {
        // Ignore errors on logout
      });
    }

    setUser(null);
    setToken(null);
    setReporterUser(undefined);
    clearFaroUserContext();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    delete axiosClient.defaults.headers.common['Authorization'];
    navigate('/login');
  };

  // Proactively refresh the access token shortly before it expires so the user
  // never hits an expired-token error in the first place.
  useEffect(() => {
    if (!token) {
      return;
    }
    const ms = msUntilRefresh(token);
    if (ms === null) {
      return;
    }
    const timer = setTimeout(() => {
      refreshAuthTokens({ client: axiosClient, refreshUrl: '/api/auth/refresh' })
        .then((newToken) => setToken(newToken))
        .catch(() => logout());
    }, Math.max(0, ms));
    return () => clearTimeout(timer);
  }, [token]);

  return (
    <AuthContext.Provider
      value={{ user, token, login, register, setSession, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Wrap App with AuthProvider
export const withAuth = (Component: React.FC) => {
  return (props: any) => (
    <AuthProvider>
      <Component {...props} />
    </AuthProvider>
  );
};
